import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { deleteChat, getChat, getChatGallery, getCommonGroups, listMembers, listRooms, removeMember, CHAT_TITLE_MAX_LENGTH, setChatAvatar, setChatRoomsEnabled, setChatTitle, setMemberRole, updateRoom } from '../api/chats';
import type { ChatRoomDto, GalleryKind, PinScope } from '../api/chats';
import { getPresence } from '../api/presence';
import { addContact, isContact } from '../api/contacts';
import { imageSrc, thumbnailSrc, uploadMedia } from '../api/media';
import { ApiError } from '../api/client';
import { formatPresence, PRESENCE_POLL_MS } from '../lib/presence';
import { formatSize } from '../lib/format';
import { requestTransferAuthorization, showTransferNotice } from '../lib/transferPolicy';
import { mergePinnedMessages } from '../lib/pinned';
import { galleryItems } from '../lib/gallery';
import { firstUrl } from '../lib/url';
import type { ChatDto, ChatSummary, MediaItem, MemberDto, MessageDto, OpenLightbox, PresenceDto, UserDto } from '../api/types';
import Avatar from './Avatar';
import RoomAvatar from './RoomAvatar';
import AvatarEditor from './AvatarEditor';
import ConfirmModal from './ConfirmModal';
import OwnerLeaveModal from './OwnerLeaveModal';
import AddMembersModal from './AddMembersModal';
import InviteLinkModal from './InviteLinkModal';
import { RoomEditModal } from './RoomsColumn';
import { notifyRoomsChanged } from '../lib/roomsEvents';
import EmojiText from './EmojiText';
import { AudioPlayer, DocumentCard, VideoVoiceRow } from './MediaWidgets';

interface Props {
  chatId: string;
  me: UserDto | null;
  onClose: () => void;
  onAvatarChanged: () => void;
  /** Чат удалён или мы из него вышли — надо обновить список и закрыть сам чат. */
  onLeft: () => void;
  /** Открыть медиа (картинка/видео) поверх всего, как из ленты — панель при этом не закрывается. */
  onOpenLightbox: OpenLightbox;
  /** Лайтбокс сейчас открыт (поверх всего, включая эту панель) — прячем свой заголовок,
   *  чтобы "Информация" и кнопка "Закрыть" не проглядывали через полупрозрачный фон. */
  lightboxOpen: boolean;
  /** Открыть закреплённое сообщение "в новом окне" (с возможностью ответить) — панель не закрывается. */
  onPreviewPinned: (msg: MessageDto) => void;
  /** Перейти в общую группу (вкладка "Группы") — закрывает панель и переключает чат. */
  onOpenChat: (chatId: string) => void;
  /**
   * Закреплённые сообщения приходят пропсами из ChatView (единый источник состояния,
   * т.к. там же есть баннер закреплённого над лентой) — если бы каждый компонент грузил
   * их сам, открепление в одном месте не отражалось бы в другом, пока не переоткрыть панель.
   */
  pinnedShared: MessageDto[];
  pinnedPersonal: MessageDto[];
  onTogglePin: (id: number, scope: PinScope, pinned: boolean) => void;
  /** Контекстное меню файлов в галерее (пункт "Перейти к сообщению") — закрывает и панель,
   *  и это меню, затем прокручивает и подсвечивает сообщение в ленте (см. ChatView). */
  onJumpToMessage: (id: number) => void;
  /** Активная комната группового чата (null — общая) — при открытой комнате профиль
   *  инфопанели показывает её (аватар/имя), а не саму группу (см. отчёт пользователя
   *  "изменение аватарки комнаты в инфопанели"). */
  roomId?: string | null;
}

type TabKind = GalleryKind | 'groups';

const GALLERY_TABS: { kind: GalleryKind; labelKey: string }[] = [
  { kind: 'media', labelKey: 'tabs.media' },
  { kind: 'music', labelKey: 'tabs.music' },
  { kind: 'voice', labelKey: 'tabs.voice' },
  { kind: 'file', labelKey: 'tabs.files' },
  { kind: 'link', labelKey: 'tabs.links' },
];

/**
 * Сообщение может нести несколько вложений разом (см. AttachPreviewModal в ChatView) —
 * бэкенд отдаёт галерею по сообщениям (см. findGalleryByKind: EXISTS по связанным файлам), а
 * тут на клиенте разворачиваем каждое сообщение в отдельные элементы галереи, по одному
 * на подходящее вложение, чтобы, например, 3 фото из одного сообщения дали 3 плитки в сетке.
 */
type GalleryEntry = { msg: MessageDto; media: MediaItem };

function flattenGallery(messages: MessageDto[], tab: GalleryKind): GalleryEntry[] {
  const out: GalleryEntry[] = [];
  for (const msg of messages) {
    for (const media of galleryItems(msg, tab)) {
      out.push({ msg, media });
    }
  }
  return out;
}

function GalleryThumb({
  media, onOpen, messageId, senderId, onContextMenu,
}: { media: MediaItem; onOpen: OpenLightbox; messageId: number; senderId: string; onContextMenu: (e: React.MouseEvent) => void }) {
  const { t } = useTranslation('chatInfo');
  const kind = media.kind === 'video' ? 'video' : 'image';
  // Сетка "Медиа" разом показывает десятки превью — под каждое тянуть полноразмерный
  // оригинал (для видео — сразу инициировать закачку файла через <video src>) расточительно.
  // Показываем только миниатюру (кадр видео/уменьшенную картинку, /thumbnail), а оригинал —
  // лишь при открытии лайтбокса по клику.
  const [thumb, setThumb] = useState<string | null>(null);
  // Кадра для видео может не быть (старое видео до этой фичи, ffmpeg недоступен на сервере —
  // см. MediaService.extractVideoFrame) — тогда /thumbnail отвечает 404. "loaded" отдельно от
  // "thumb": как только попытка завершилась, плитка должна стать кликабельной, а не зависать
  // на спиннере навечно (для картинок 404 не бывает — сервер отдаёт оригинал как есть).
  const [loaded, setLoaded] = useState(false);
  const [opening, setOpening] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => () => {
    aliveRef.current = false;
  }, []);
  useEffect(() => {
    setThumb(null);
    setLoaded(false);
    if (!media.fileId) return;
    // thumbnailSrc кэширует blob-URL по fileId на весь срок жизни вкладки (см. api/media.ts) —
    // URL общий для всех виджетов этого fileId, поэтому здесь его больше НЕ ревокаем.
    let alive = true;
    thumbnailSrc(media.fileId)
      .then((u) => {
        if (alive) setThumb(u);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [media.fileId]);

  const handleOpen = async () => {
    if (!media.fileId || opening) return;
    setOpening(true);
    try {
      if (kind === 'image') {
        const authorization = await requestTransferAuthorization({
          direction: 'download',
          label: t('transfer.originalPhoto'),
          sizeBytes: media.sizeBytes ?? null,
        });
        if (!authorization.allowed) {
          showTransferNotice(authorization.reason ?? t('transfer.wifi'));
          return;
        }
      }
      const full = await imageSrc(media.fileId);
      if (aliveRef.current) onOpen(full, kind, messageId, senderId, media.fileId);
    } catch {
      /* не критично — просто не откроется в этот раз */
    } finally {
      if (aliveRef.current) setOpening(false);
    }
  };

  // Кружочек (видео-сообщение, записанное кнопкой записи) — как и в самой ленте
  // (см. isRoundVideoMsg в ChatView), отличается от обычного прикреплённого видео
  // отсутствием fileName. В галерее должен остаться круглым, а не квадратным превью.
  const isRound = media.kind === 'video' && !media.fileName;
  if (!loaded) return <div className="info-gallery-thumb info-gallery-thumb-loading" />;
  return (
    <div
      className={'info-gallery-thumb' + (isRound ? ' round' : '')}
      onClick={handleOpen}
      onContextMenu={onContextMenu}
      style={{ cursor: opening ? 'wait' : 'pointer' }}
    >
      {thumb && <img src={thumb} alt="" />}
      {kind === 'video' && <div className="video-round-play"><img src="/play.svg" alt="" /></div>}
    </div>
  );
}

/** Строка "Музыка" — прикреплённый аудиофайл: имя/размер (иконка слева скачивает) и плеер снизу. */
function GalleryMusicRow({ media, onContextMenu }: { media: MediaItem; onContextMenu: (e: React.MouseEvent) => void }) {
  const { t } = useTranslation('chatInfo');
  const [src, setSrc] = useState<string | null>(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  useEffect(() => {
    if (!media.fileId) return;
    let alive = true;
    imageSrc(media.fileId).then((u) => alive && setSrc(u)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [media.fileId]);
  const download = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!media.fileId || downloadBusy) return;
    setDownloadBusy(true);
    try {
      const authorization = await requestTransferAuthorization({
        direction: 'download',
        label: media.fileName || t('fallback.audioFile'),
        sizeBytes: media.sizeBytes ?? null,
      });
      if (!authorization.allowed) {
        showTransferNotice(authorization.reason ?? t('transfer.wifi'));
        return;
      }
      const url = src ?? await imageSrc(media.fileId);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = media.fileName || 'audio';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      showTransferNotice(t('transfer.audioFailed'));
    } finally {
      setDownloadBusy(false);
    }
  };
  return (
    <div className="info-audio-row" onContextMenu={onContextMenu}>
      <div className="info-audio-row-head">
        <a className="doc-icon-wrap" href={src ?? undefined} download={media.fileName} onClick={download} aria-disabled={downloadBusy}>
          <img src="/audio-file.svg" alt="" className="doc-icon" />
          <span className="doc-download-hint"><img src="/download.svg" alt="" /></span>
        </a>
        <div className="doc-info" style={{ flex: 1, minWidth: 0 }}>
          <div className="doc-name">{media.fileName || t('fallback.music')}</div>
          <div className="doc-size">{formatSize(media.sizeBytes)}</div>
        </div>
      </div>
      <AudioPlayer fileId={media.fileId} fileName={media.fileName} sizeBytes={media.sizeBytes} showRate={false} />
    </div>
  );
}

/** Строка "Голосовые" — только прослушивание, без скачивания (как и в самой ленте). */
function GalleryVoiceRow({ media, onContextMenu }: { media: MediaItem; onContextMenu: (e: React.MouseEvent) => void }) {
  // Видео-сообщение (кружок) — та же строка, что у голосовых, только вместо иконки в
  // круглой play-кнопке — кадр видео (см. VideoVoiceRow и отчёт пользователя "видео кружки
  // отображать так же как аудио сообщения только с миниатюрой вместо иконки").
  if (media.kind === 'video') {
    return (
      <div className="info-audio-row" onContextMenu={onContextMenu}>
        <VideoVoiceRow fileId={media.fileId} />
      </div>
    );
  }
  return (
    <div className="info-audio-row" onContextMenu={onContextMenu}>
      <AudioPlayer fileId={media.fileId} sizeBytes={media.sizeBytes} />
    </div>
  );
}

function GalleryFileRow({ media, onContextMenu }: { media: MediaItem; onContextMenu: (e: React.MouseEvent) => void }) {
  return (
    <div onContextMenu={onContextMenu}>
      <DocumentCard fileId={media.fileId} fileName={media.fileName} sizeBytes={media.sizeBytes} />
    </div>
  );
}

/** Короткий текстовый превью сообщения для строки "закреплённые" (медиа — плейсхолдер). */
function pinnedPreviewText(msg: MessageDto): string {
  const mediaList = msg.payload.data?.media ?? [];
  if (mediaList.length > 1) return i18n.t('chatInfo:pinned.attachments', { count: mediaList.length });
  const media = mediaList[0];
  if (media?.kind === 'audio') return media.fileName ? '🎵 ' + media.fileName : i18n.t('chatInfo:pinned.voice');
  if (media?.kind === 'video') return i18n.t('chatInfo:pinned.video');
  if (media?.kind === 'file') return '📄 ' + (media.fileName || i18n.t('chatInfo:fallback.file'));
  if (media?.kind === 'image') return i18n.t('chatInfo:pinned.photo');
  return msg.payload.data?.text || '';
}

function PinnedRow({ msg, scope, onClick, onUnpin }: { msg: MessageDto; scope: PinScope; onClick: () => void; onUnpin: () => void }) {
  const { t } = useTranslation('chatInfo');
  return (
    <div className="info-file-row" onClick={onClick}>
      <img src="/pin.svg" alt="" className="doc-icon" />
      <div className="doc-info" style={{ flex: 1, minWidth: 0 }}>
        <div className="doc-name">{pinnedPreviewText(msg)}</div>
        <div className="doc-size">{scope === 'me' ? t('pinned.forMe') : t('pinned.forAll')}</div>
      </div>
      <button
        type="button"
        className="pinned-banner-close"
        title={t('pinned.unpin')}
        onClick={(e) => {
          e.stopPropagation();
          onUnpin();
        }}
      >
        ✕
      </button>
    </div>
  );
}

function GalleryLinkRow({ msg }: { msg: MessageDto }) {
  const text = msg.payload.data?.text || '';
  const url = firstUrl(text) || '';
  const preview = msg.payload.data?.linkPreview;
  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    // не URL-подобный текст — оставим как есть
  }
  return (
    <a className="info-link-row" href={url} target="_blank" rel="noreferrer">
      <div className="info-link-title">{preview?.title || host}</div>
      <div className="info-link-url">{url}</div>
    </a>
  );
}

function GroupRow({ g, onClick }: { g: ChatSummary; onClick: () => void }) {
  const { t } = useTranslation('chatInfo');
  return (
    <div className="chat-info-member" style={{ cursor: 'pointer' }} onClick={onClick}>
      <Avatar userId={g.otherUserId} chatId={g.id} name={g.title || t('fallback.group')} size={40} />
      <div>
        <div style={{ fontSize: 14 }}>{g.title || t('fallback.group')}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{g.membersPreview.length ? g.membersPreview.join(', ') : t('fallback.group')}</div>
      </div>
    </div>
  );
}

export default function ChatInfoModal({
  chatId, me, onClose, onAvatarChanged, onLeft, onOpenLightbox, lightboxOpen, onPreviewPinned, onOpenChat,
  pinnedShared, pinnedPersonal, onTogglePin, onJumpToMessage, roomId = null,
}: Props) {
  const { t } = useTranslation('chatInfo');
  // Контекстное меню строк галереи (медиа/музыка/голос/документы) — правый клик по файлу,
  // сейчас только "Перейти к сообщению" (см. отчёт пользователя "добавить контекстное меню
  // по работе с файлами из инфо панели - добавить функцию перехода к сообщению").
  const [fileCtxMenu, setFileCtxMenu] = useState<{ x: number; y: number; msgId: number } | null>(null);
  const openFileCtxMenu = (e: React.MouseEvent, msgId: number) => {
    e.preventDefault();
    e.stopPropagation();
    // Панель сама узкая и обычно у правого края экрана — без клэмпа меню могло вылезти
    // за пределы окна и обрезаться (см. отчёт: правый клик у края панели).
    const MENU_W = 210;
    const MENU_H = 44;
    const x = Math.max(4, Math.min(e.clientX, window.innerWidth - MENU_W));
    const y = Math.max(4, Math.min(e.clientY, window.innerHeight - MENU_H));
    setFileCtxMenu({ x, y, msgId });
  };
  const [chat, setChat] = useState<ChatDto | null>(null);
  // Участники с ролью (владелец/админ) — только для группы, отдельный запрос: у ChatDto.members
  // (см. getChat) роли нет, это плоский UserBriefDto[] (см. api/chats.ts listMembers).
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [error, setError] = useState('');
  // Управление участниками группы (назначить/снять админа, исключить) — раньше в UI не было
  // вообще никакого способа это сделать, хотя бэкенд давно поддерживает несколько админов
  // группы (см. ChatService.setMemberRole, отчёт пользователя "дать возможность нескольким
  // пользователям назначать администраторами группы"). Владелец назначает/снимает и исключает
  // кого угодно (кроме себя), админ может только исключать обычных участников (других админов
  // не трогает — ни понизить, ни исключить; см. бэкенд, решение 2026-09-29).
  const [memberMenuOpenId, setMemberMenuOpenId] = useState<string | null>(null);
  // Координаты меню участника во вьюпорте: absolute-меню обрезалось overflow списка/блока профиля
  // и пряталось под вкладками Медиа/Музыка (I25).
  const [memberMenuPos, setMemberMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [kickTarget, setKickTarget] = useState<MemberDto | null>(null);
  const [ownerTarget, setOwnerTarget] = useState<MemberDto | null>(null);
  const [memberActionError, setMemberActionError] = useState('');
  // Комнаты группы — только чтобы найти текущую (roomId) и показать/сменить именно её
  // аватар в профиле панели (см. RoomsColumn — там комнатами управляют, тут только аватар
  // активной, раз панель и так уже открыта на ней).
  const [rooms, setRooms] = useState<ChatRoomDto[]>([]);
  const [roomAvatarBusy, setRoomAvatarBusy] = useState(false);
  const [roomAvatarError, setRoomAvatarError] = useState('');
  // Отдельно от pendingAvatarFile ниже (тот — для аватара самой группы) — файл ждёт кропа в
  // AvatarEditor перед загрузкой (см. отчёт пользователя "нет возможности настроить аватарку").
  const [pendingRoomAvatarFile, setPendingRoomAvatarFile] = useState<File | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  // Вкл/выкл "комнат" — раньше решалось только один раз при создании группы (см. отчёт
  // пользователя "настройку для комнат перенеси в настройки самой группы").
  const [roomsEnabledBusy, setRoomsEnabledBusy] = useState(false);
  const [roomsEnabledError, setRoomsEnabledError] = useState('');
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  // Меню действий над группой (три точки в заголовке) — покинуть/удалить группу спрятаны
  // сюда, а не торчат отдельными кнопками в теле панели.
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  // Переименование группы — тот же приём, что у переименования папки в SettingsModal:
  // имя в профиле на время правки становится полем ввода (Enter/уход фокуса — сохранить,
  // Escape — отмена), без отдельной кнопки "Сохранить" в теле панели.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  // Escape снимает поле ввода, и при размонтировании прилетает blur со старым замыканием
  // (renaming=true) — без этого флага отменённая правка всё равно сохранилась бы.
  const renameCancelledRef = useRef(false);
  const [presence, setPresence] = useState<PresenceDto | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabKind>('media');
  // "Увеличенный" вид категории — плашка вкладок остаётся видна и позволяет переключаться
  // между категориями напрямую, без возврата назад; сворачивается только профиль/закреп.
  const [categoryOpen, setCategoryOpen] = useState(false);
  const tabRefs = useRef<Map<TabKind, HTMLButtonElement>>(new Map());
  const [pillRect, setPillRect] = useState<{ left: number; width: number } | null>(null);
  // Подсветка вкладки должна откликаться сразу по клику/отпусканию, не дожидаясь, пока
  // доедет контент (см. switchTab/onViewportPointerUp) — а сам контент едет следом.
  const [displayTab, setDisplayTab] = useState<TabKind>('media');
  useEffect(() => {
    setDisplayTab(selectedTab);
  }, [selectedTab]);
  // Перелистывание содержимого вкладок — настоящий горизонтальный scroll-snap (как у
  // нативного свайпа), а не имитация transform-анимацией: три слайда (сосед слева,
  // текущий, сосед справа) лежат в ряд, потянуть можно и мышью, и тачем/трекпадом
  // (см. onViewportPointer*/renderTabBody ниже). Клик по вкладке, не являющейся соседней
  // в списке, временно подставляет её в нужный слот через transitionTarget и доезжает
  // туда smooth-скроллом — тот же самый механизм, что и у перетаскивания.
  const viewportRef = useRef<HTMLDivElement>(null);
  const [transitionTarget, setTransitionTarget] = useState<{ kind: TabKind; dir: 1 | -1 } | null>(null);
  const [panning, setPanning] = useState(false);
  // Актуальные значения для сторожевого таймера скролла (см. ниже) — он подписывается на
  // 'scroll' один раз на categoryOpen/вкладку, а не пересоздаётся при каждом panning/
  // transitionTarget, поэтому читает их через рефы, а не напрямую из замыкания.
  const panningRef = useRef(panning);
  useEffect(() => { panningRef.current = panning; }, [panning]);
  const transitionTargetRef = useRef(transitionTarget);
  useEffect(() => { transitionTargetRef.current = transitionTarget; }, [transitionTarget]);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; startScrollLeft: number; axis: 'x' | 'y' | null } | null>(null);
  const suppressClickRef = useRef(false);
  const [galleryItems, setGalleryItems] = useState<Record<GalleryKind, MessageDto[]>>({
    media: [], music: [], voice: [], file: [], link: [],
  });
  const [galleryCursor, setGalleryCursor] = useState<Record<GalleryKind, number | null | undefined>>({
    media: undefined,
    music: undefined,
    voice: undefined,
    file: undefined,
    link: undefined,
  });
  const [galleryLoading, setGalleryLoading] = useState<Record<GalleryKind, boolean>>({
    media: false, music: false, voice: false, file: false, link: false,
  });
  const [groups, setGroups] = useState<ChatSummary[] | null>(null);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const allPinned = useMemo(
    () => mergePinnedMessages(pinnedShared, pinnedPersonal),
    [pinnedShared, pinnedPersonal],
  );

  // chat.members — плоский UserBriefDto[] (см. api/types.ts ChatDto), без обёртки user/role.
  // Моя роль в группе (1=владелец,2=админ,3=участник) — определяет, какие действия над
  // другими участниками мне доступны (см. promoteMember/demoteMember/doKickMember).
  const myRole = members?.find((m) => m.user.id === me?.id)?.role ?? null;
  const activeRoom = roomId ? rooms.find((r) => r.id === roomId) ?? null : null;
  const otherMember = chat && chat.type === 1 ? chat.members.find((m) => m.id === chat.otherUserId) : null;
  // У личных чатов chat.title с бэкенда обычно пустой (заголовок — это имя собеседника,
  // а не свойство самого чата) — берём его из списка участников.
  const displayTitle = chat?.title || otherMember?.displayName || t('fallback.chat');
  const tabs: { kind: TabKind; labelKey: string }[] = chat?.type === 1
    ? [...GALLERY_TABS, { kind: 'groups', labelKey: 'tabs.groups' }]
    : GALLERY_TABS;
  const selectedTabMeta = tabs.find((t) => t.kind === selectedTab);

  // Соседние вкладки, которые рендерятся вместе с текущей (слева/справа) для scroll-snap
  // перелистывания. Если идёт клик-переход не на прямого соседа по списку — transitionTarget
  // временно подменяет нужный слот целевой вкладкой, доехать нужно ровно на один "шаг".
  const activeIdx = tabs.findIndex((t) => t.kind === selectedTab);
  const naturalPrev = activeIdx > 0 ? tabs[activeIdx - 1].kind : null;
  const naturalNext = activeIdx < tabs.length - 1 ? tabs[activeIdx + 1].kind : null;
  const slidePrev = transitionTarget?.dir === -1 ? transitionTarget.kind : naturalPrev;
  const slideNext = transitionTarget?.dir === 1 ? transitionTarget.kind : naturalNext;

  // Скользящая плашка под активной вкладкой (подсветка) — измеряем позицию реальной
  // кнопки, а не считаем проценты вручную: список вкладок разный для личных/групповых чатов.
  // Сама полоска вкладок тоже скроллится вбок (см. .info-tabs) — активную вкладку стараемся
  // держать по центру видимой области (она к тому же крупнее соседних, см. .info-tab.active),
  // чтобы по бокам было видно по соседней вкладке. У крайних вкладок списка центрировать
  // некуда — clamp к границам скролла сам не даёт утянуть строку дальше самого края.
  useLayoutEffect(() => {
    const el = tabRefs.current.get(displayTab);
    const row = el?.parentElement;
    if (el && row) {
      setPillRect({ left: el.offsetLeft, width: el.offsetWidth });
      const target = el.offsetLeft + el.offsetWidth / 2 - row.clientWidth / 2;
      const maxScroll = row.scrollWidth - row.clientWidth;
      row.scrollTo({ left: Math.max(0, Math.min(maxScroll, target)), behavior: 'smooth' });
    }
  }, [displayTab, categoryOpen, tabs.length]);

  // Переключение на другой чат без закрытия панели (клик по чату в списке слева) не
  // размонтирует ChatInfoModal — компонент просто получает новый chatId проп. Без явного
  // сброса тут галерея/общие группы/ошибка от ПРЕЖНЕГО чата продолжали бы показываться
  // поверх нового (см. отчёт: "открытая вкладка Медиа продолжает показывать файлы прежнего
  // чата"). selectedTab тоже сбрасываем — иначе, например, выбранная вкладка "Группы" у
  // личного чата может оказаться невалидной для группового чата, где такой вкладки нет.
  useEffect(() => {
    setChat(null);
    setError('');
    setGalleryItems({ media: [], music: [], voice: [], file: [], link: [] });
    setGalleryCursor({ media: undefined, music: undefined, voice: undefined, file: undefined, link: undefined });
    setGalleryLoading({ media: false, music: false, voice: false, file: false, link: false });
    setGroups(null);
    setSelectedTab('media');
    setCategoryOpen(false);
  }, [chatId]);

  useEffect(() => {
    getChat(chatId)
      .then(setChat)
      .catch(() => setError(t('error.load')));
  }, [chatId, t]);

  useEffect(() => {
    if (chat?.type !== 2) {
      setMembers(null);
      return;
    }
    listMembers(chatId)
      .then(setMembers)
      .catch(() => {
        /* не критично — секция участников просто не покажет роли */
      });
  }, [chatId, chat?.type]);

  useEffect(() => {
    setRooms([]);
    if (!chat?.hasRooms) return;
    let alive = true;
    listRooms(chatId).then((r) => alive && setRooms(r)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [chatId, chat?.hasRooms]);

  // Онлайн-статус собеседника — только в личных чатах, обновляется, пока панель открыта.
  useEffect(() => {
    if (!chat || chat.type !== 1 || !chat.otherUserId) {
      setPresence(null);
      return;
    }
    const otherUserId = chat.otherUserId;
    let alive = true;
    const poll = () => {
      getPresence(otherUserId)
        .then((p) => alive && setPresence(p))
        .catch(() => {});
    };
    poll();
    const timer = window.setInterval(poll, PRESENCE_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [chat]);

  // Кнопка «Добавить в контакты» в профиле собеседника — работает и после того, как баннер
  // в самом чате отклонили кнопкой «Игнорировать» (см. ChatView, отчёт пользователя "добавить
  // кнопку добавить в контакты даже если игнорируется") — не завязана на shouldPromptToAdd.
  const [isContactState, setIsContactState] = useState<boolean | null>(null);
  const [contactBusy, setContactBusy] = useState(false);
  useEffect(() => {
    setIsContactState(null);
    if (!chat || chat.type !== 1 || !chat.otherUserId) return;
    const otherUserId = chat.otherUserId;
    let alive = true;
    isContact(otherUserId)
      .then((v) => alive && setIsContactState(v))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [chat]);
  const addContactFromInfo = async () => {
    if (!chat?.otherUserId || contactBusy) return;
    setContactBusy(true);
    try {
      await addContact(chat.otherUserId);
      setIsContactState(true);
    } catch {
      /* не критично — кнопка просто останется, можно попробовать снова */
    } finally {
      setContactBusy(false);
    }
  };

  // Первая страница текущей вкладки грузится сразу (не дожидаясь клика) — иначе свёрнутая
  // панель не может показать превью (десяток иконок медиа и т.п., см. renderCollapsedPreview).
  // Пока категория развёрнута, заодно подгружаем и соседние по скроллу вкладки — иначе при
  // перетаскивании к соседу мы бы на мгновение показывали пустую вкладку до её загрузки.
  useEffect(() => {
    if (!chat) return;
    const kinds = Array.from(new Set(categoryOpen ? [slidePrev, selectedTab, slideNext] : [selectedTab])).filter(
      (k): k is GalleryKind => !!k && k !== 'groups',
    );
    kinds.forEach((k) => {
      if (galleryCursor[k] !== undefined || galleryLoading[k]) return;
      setGalleryLoading((prev) => ({ ...prev, [k]: true }));
      getChatGallery(chatId, k)
        .then((page) => {
          setGalleryItems((prev) => ({ ...prev, [k]: page.items }));
          setGalleryCursor((prev) => ({ ...prev, [k]: page.nextBefore }));
        })
        .catch(() => setGalleryCursor((prev) => ({ ...prev, [k]: null })))
        .finally(() => setGalleryLoading((prev) => ({ ...prev, [k]: false })));
    });
  }, [chatId, chat, categoryOpen, selectedTab, slidePrev, slideNext, galleryCursor, galleryLoading]);

  // Вкладка "Группы" — не галерея вложений, отдельный источник данных, грузится один раз.
  useEffect(() => {
    if (selectedTab !== 'groups' || groups !== null || groupsLoading) return;
    setGroupsLoading(true);
    getCommonGroups(chatId)
      .then(setGroups)
      .catch(() => setGroups([]))
      .finally(() => setGroupsLoading(false));
  }, [selectedTab, chatId, groups, groupsLoading]);

  const loadMoreGallery = (tab: GalleryKind) => {
    const before = galleryCursor[tab];
    if (!before || galleryLoading[tab]) return;
    setGalleryLoading((prev) => ({ ...prev, [tab]: true }));
    getChatGallery(chatId, tab, before)
      .then((page) => {
        setGalleryItems((prev) => ({ ...prev, [tab]: [...prev[tab], ...page.items] }));
        setGalleryCursor((prev) => ({ ...prev, [tab]: page.nextBefore }));
      })
      .catch(() => {})
      .finally(() => setGalleryLoading((prev) => ({ ...prev, [tab]: false })));
  };

  // Клик по вкладке в плашке — если это не прямой сосед текущей по списку, временно
  // подставляем цель в нужный слот (см. slidePrev/slideNext) и доезжаем туда smooth-скроллом
  // в эффекте ниже; сам selectedTab поменяется только по факту прибытия (onscrollend).
  const switchTab = (kind: TabKind) => {
    if (!categoryOpen) {
      setSelectedTab(kind);
      setDisplayTab(kind);
      setCategoryOpen(true);
      return;
    }
    if (kind === selectedTab || transitionTarget) return;
    const curIdx = tabs.findIndex((t) => t.kind === selectedTab);
    const targetIdx = tabs.findIndex((t) => t.kind === kind);
    setDisplayTab(kind);
    setTransitionTarget({ kind, dir: targetIdx > curIdx ? 1 : -1 });
  };

  // Доезд до подставленной кликом вкладки — smooth-скролл к её слоту, коммит selectedTab
  // по факту завершения (scrollend, с таймаутом-фолбэком для браузеров без этого события).
  useEffect(() => {
    if (!transitionTarget) return;
    const el = viewportRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const idx = transitionTarget.dir === 1 ? (slidePrev ? 2 : 1) : 0;
    const frame = requestAnimationFrame(() => el.scrollTo({ left: idx * w, behavior: 'smooth' }));
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      // Жёстко фиксируем точный пиксель перед коммитом — smooth-scrollTo не гарантирует
      // приземление ровно на idx*w (особенно если нативный scroll-snap на тачскрине успел
      // чуть "поспорить" с ним по дороге), а мелкая невязка за много перелистываний туда-
      // обратно не гасится, а копится в одну сторону — контент визуально "съезжает".
      el.scrollLeft = idx * (el.clientWidth || 1);
      setSelectedTab(transitionTarget.kind);
      setCategoryOpen(true);
      setTransitionTarget(null);
    };
    const supportsScrollEnd = 'onscrollend' in el;
    if (supportsScrollEnd) el.addEventListener('scrollend', finish, { once: true });
    const timer = window.setTimeout(finish, 400);
    return () => {
      cancelAnimationFrame(frame);
      if (supportsScrollEnd) el.removeEventListener('scrollend', finish);
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionTarget]);

  // Без активного клик-перехода — держим scrollLeft ровно на текущей вкладке. Срабатывает
  // и на первом открытии категории, и сразу после коммита клика/перетаскивания: к этому
  // моменту viewport уже физически стоит на нужном слайде, так что смена системы координат
  // (индекс слайда меняется вместе с selectedTab) происходит без прыжка — просто мгновенно
  // выравниваем то, что и так уже видно.
  useLayoutEffect(() => {
    if (transitionTarget) return;
    const el = viewportRef.current;
    if (!el || !categoryOpen) return;
    const w = el.clientWidth || 1;
    el.scrollLeft = (slidePrev ? 1 : 0) * w;
  }, [selectedTab, categoryOpen, transitionTarget, slidePrev]); // slidePrev производна от selectedTab/transitionTarget — лишних запусков нет

  // Сторожевой таймер поверх обеих ручных доводок выше: если ПОСЛЕ любого скролла (по любой
  // причине — не только наш свайп/клик, а и то, что мы не смогли воспроизвести и объяснить
  // на конкретных устройствах: другой движок тачскрина/WebView, особенности scroll-snap или
  // scroll anchoring там, куда мы не заглядывали) панель "успокоилась" не ровно на границе
  // слайда — досчитываем сами, а не полагаемся на то, что причина дрейфа исчерпывающе понята.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !categoryOpen) return;
    let timer: number | null = null;
    const onScroll = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (panningRef.current || transitionTargetRef.current) return;
        const w = el.clientWidth || 1;
        const idx = Math.round(el.scrollLeft / w);
        const exact = idx * w;
        if (Math.abs(el.scrollLeft - exact) > 0.5) el.scrollLeft = exact;
        const slots = [slidePrev, selectedTab, slideNext].filter((k): k is TabKind => k !== null);
        const target = slots[Math.max(0, Math.min(slots.length - 1, idx))];
        if (target && target !== selectedTab) {
          setSelectedTab(target);
          setCategoryOpen(true);
        }
      }, 120);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (timer) window.clearTimeout(timer);
    };
  }, [categoryOpen, slidePrev, selectedTab, slideNext]);

  // Перетаскивание вкладок мышью/тачем/пером — pointer capture ловит горизонтальный жест,
  // вертикальный (обычный скролл панели) не трогаем и отдаём браузеру как есть. Короткий тап
  // (движение ниже порога) не трогаем вовсе — клики по строкам файлов/ссылке "Скачать"/кнопкам
  // плеера продолжают работать как обычно; подавляем клик только если жест реально стал
  // горизонтальным драгом (см. suppressClickRef/onViewportClickCapture). Не перехватываем
  // только настоящие range/видео/аудио-элементы с собственным нативным перетаскиванием —
  // у самих виджетов плеера их нет (см. MediaWidgets: <audio> скрыт, сик — по клику).
  const onViewportPointerDown = (e: React.PointerEvent) => {
    if (transitionTarget) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, video, audio')) return;
    const el = viewportRef.current;
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, startScrollLeft: el?.scrollLeft ?? 0, axis: null };
  };

  const onViewportPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const el = viewportRef.current;
    if (!d || !el) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (d.axis === 'x') {
        try {
          el.setPointerCapture(d.pointerId);
        } catch {
          // не критично — жест продолжит работать и без захвата указателя
        }
        suppressClickRef.current = true;
        setPanning(true);
      } else {
        dragRef.current = null;
        return;
      }
    }
    if (d.axis !== 'x') return;
    e.preventDefault();
    el.scrollLeft = d.startScrollLeft - dx;
  };

  const onViewportPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    const el = viewportRef.current;
    if (!el || !d || d.axis !== 'x') {
      setPanning(false);
      return;
    }
    try {
      el.releasePointerCapture(d.pointerId);
    } catch {
      // жест уже мог завершиться сам — capture не критичен
    }
    const w = el.clientWidth || 1;
    const slots = [slidePrev, selectedTab, slideNext].filter((k): k is TabKind => k !== null);
    const idx = Math.max(0, Math.min(slots.length - 1, Math.round(el.scrollLeft / w)));
    const target = slots[idx];
    el.scrollTo({ left: idx * w, behavior: 'smooth' });
    if (target !== selectedTab) setDisplayTab(target);
    // panning (и вместе с ним отключённый CSS scroll-snap) держим до самого приземления
    // доводки, а не сбрасываем сразу по pointerup — иначе mandatory scroll-snap успевает
    // "поспорить" с нашим smooth-scrollTo прямо во время анимации, и на части
    // тачскринов/планшетных WebView конечная позиция чуть промахивается мимо idx*w. За много
    // перелистываний туда-обратно такие промахи не гасятся, а копятся в одну сторону —
    // контент визуально "съезжает". scrollend не сработает, если целевая позиция уже
    // совпадает с текущей (браузер сам клампит scrollLeft к границам во время протяжки за
    // край — scrollTo тогда холостой) — без таймаута-подстраховки коммит завис бы навсегда.
    let done = false;
    const settle = () => {
      if (done) return;
      done = true;
      el.removeEventListener('scrollend', handler);
      el.scrollLeft = idx * (el.clientWidth || 1);
      if (target !== selectedTab) {
        setSelectedTab(target);
        setCategoryOpen(true);
      }
      setPanning(false);
    };
    const handler = () => settle();
    el.addEventListener('scrollend', handler);
    window.setTimeout(settle, 300);
  };

  const onViewportClickCapture = (e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const renderTabBody = (tab: TabKind): React.ReactNode => (
    <>
      {tab === 'media' && (
        galleryItems.media.length === 0 && galleryCursor.media === null ? (
          <div className="info-empty">{t('empty.media')}</div>
        ) : (
          <div className="info-gallery-grid">
            {flattenGallery(galleryItems.media, 'media').map(({ msg, media }, i) => (
              <GalleryThumb
                key={msg.id + ':' + i}
                media={media}
                onOpen={onOpenLightbox}
                messageId={msg.id}
                senderId={msg.senderId}
                onContextMenu={(e) => openFileCtxMenu(e, msg.id)}
              />
            ))}
          </div>
        )
      )}
      {tab === 'music' && (
        galleryItems.music.length === 0 && galleryCursor.music === null ? (
          <div className="info-empty">{t('empty.music')}</div>
        ) : (
          <div className="info-file-list">
            {flattenGallery(galleryItems.music, 'music').map(({ msg, media }, i) => (
              <GalleryMusicRow key={msg.id + ':' + i} media={media} onContextMenu={(e) => openFileCtxMenu(e, msg.id)} />
            ))}
          </div>
        )
      )}
      {tab === 'voice' && (
        galleryItems.voice.length === 0 && galleryCursor.voice === null ? (
          <div className="info-empty">{t('empty.voice')}</div>
        ) : (
          <div className="info-file-list">
            {flattenGallery(galleryItems.voice, 'voice').map(({ msg, media }, i) => (
              <GalleryVoiceRow key={msg.id + ':' + i} media={media} onContextMenu={(e) => openFileCtxMenu(e, msg.id)} />
            ))}
          </div>
        )
      )}
      {tab === 'file' && (
        galleryItems.file.length === 0 && galleryCursor.file === null ? (
          <div className="info-empty">{t('empty.files')}</div>
        ) : (
          <div className="info-file-list">
            {flattenGallery(galleryItems.file, 'file').map(({ msg, media }, i) => (
              <GalleryFileRow key={msg.id + ':' + i} media={media} onContextMenu={(e) => openFileCtxMenu(e, msg.id)} />
            ))}
          </div>
        )
      )}
      {tab === 'link' && (
        galleryItems.link.length === 0 && galleryCursor.link === null ? (
          <div className="info-empty">{t('empty.links')}</div>
        ) : (
          <div className="info-link-list">
            {galleryItems.link.map((m) => <GalleryLinkRow key={m.id} msg={m} />)}
          </div>
        )
      )}
      {tab !== 'groups' && galleryCursor[tab] != null && (
        <button type="button" className="link-btn" style={{ margin: '8px 16px' }} disabled={galleryLoading[tab]} onClick={() => loadMoreGallery(tab)}>
          {galleryLoading[tab] ? t('loading') : t('showMore')}
        </button>
      )}
      {tab !== 'groups' && galleryLoading[tab] && galleryCursor[tab] === undefined && (
        <div className="info-loading"><span className="spinner" /></div>
      )}
      {tab === 'groups' && (
        groupsLoading && groups === null ? (
          <div className="info-loading"><span className="spinner" /></div>
        ) : !groups || groups.length === 0 ? (
          <div className="info-empty">{t('empty.groups')}</div>
        ) : (
          <div className="chat-info-members">
            {groups.map((g) => (
              <GroupRow key={g.id} g={g} onClick={() => onOpenChat(g.id)} />
            ))}
          </div>
        )
      )}
    </>
  );

  /**
   * Превью текущей вкладки в свёрнутом состоянии панели (профиль/закреп видны, категория
   * ещё не открыта, либо только что закрыта кнопкой "назад") — чтобы не терять контекст
   * после выхода из категории. Медиа — сеткой до 10 миниатюр (как и должно быть сразу при
   * открытии панели, ещё до первого клика по вкладке), остальные — списком до 3 строк.
   */
  const renderCollapsedPreview = (): React.ReactNode => {
    if (!selectedTabMeta) return null;
    if (selectedTab === 'groups') {
      if (!groups || groups.length === 0) return null;
      return (
        <div className="chat-info-members" style={{ marginBottom: 8 }}>
          {groups.slice(0, 3).map((g) => <GroupRow key={g.id} g={g} onClick={() => onOpenChat(g.id)} />)}
        </div>
      );
    }
    const items = galleryItems[selectedTab];
    if (!items || items.length === 0) return null;
    if (selectedTab === 'media') {
      const entries = flattenGallery(items, 'media').slice(0, 10);
      if (entries.length === 0) return null;
      return (
        <div className="info-gallery-grid" style={{ marginBottom: 8 }}>
          {entries.map(({ msg, media }, i) => (
            <GalleryThumb
              key={msg.id + ':' + i}
              media={media}
              onOpen={onOpenLightbox}
              messageId={msg.id}
              senderId={msg.senderId}
              onContextMenu={(e) => openFileCtxMenu(e, msg.id)}
            />
          ))}
        </div>
      );
    }
    if (selectedTab === 'link') {
      const rows = items.slice(0, 3);
      return (
        <div className="info-link-list" style={{ marginBottom: 8 }}>
          {rows.map((m) => <GalleryLinkRow key={m.id} msg={m} />)}
        </div>
      );
    }
    const Row = selectedTab === 'music' ? GalleryMusicRow : selectedTab === 'voice' ? GalleryVoiceRow : GalleryFileRow;
    const entries = flattenGallery(items, selectedTab).slice(0, 3);
    if (entries.length === 0) return null;
    return (
      <div className="info-file-list" style={{ marginBottom: 8 }}>
        {entries.map(({ msg, media }, i) => (
          <Row key={msg.id + ':' + i} media={media} onContextMenu={(e) => openFileCtxMenu(e, msg.id)} />
        ))}
      </div>
    );
  };

  const onAvatarFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setPendingAvatarFile(file);
  };

  const onAvatarCropped = async (blob: Blob) => {
    setPendingAvatarFile(null);
    setAvatarBusy(true);
    setAvatarError('');
    try {
      const media = await uploadMedia(blob, 'avatar.jpg');
      await setChatAvatar(chatId, media.id);
      setAvatarVersion((v) => v + 1);
      onAvatarChanged(); // обновить список чатов — иначе там останется старая картинка
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : t('error.avatar'));
      setPendingAvatarFile(new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' }));
    } finally {
      setAvatarBusy(false);
    }
  };

  // Смена аватарки активной комнаты — тот же кроп-редактор, что и у аватара группы/пользователя
  // и у RoomsColumn.RoomEditModal (см. AvatarEditor ниже); файл сначала уходит на кроп, аплоад —
  // уже из onRoomAvatarCropped, после подтверждения кадра.
  const onRoomAvatarFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setPendingRoomAvatarFile(file);
  };

  const onRoomAvatarCropped = async (blob: Blob) => {
    setPendingRoomAvatarFile(null);
    if (!activeRoom) return;
    setRoomAvatarBusy(true);
    setRoomAvatarError('');
    try {
      const media = await uploadMedia(blob, 'avatar.jpg');
      const updated = await updateRoom(chatId, activeRoom.id, { avatarFileId: media.id });
      setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setRoomAvatarError(err instanceof Error ? err.message : t('error.roomAvatar'));
      setPendingRoomAvatarFile(new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' }));
    } finally {
      setRoomAvatarBusy(false);
    }
  };

  const startRename = () => {
    setActionsMenuOpen(false);
    setRenameError('');
    setRenameValue(chat?.title ?? '');
    renameCancelledRef.current = false;
    setRenaming(true);
  };

  const commitRename = async () => {
    if (!renaming || renameCancelledRef.current) return;
    renameCancelledRef.current = true; // Enter + последующий blur — сохранить один раз
    const title = renameValue.trim();
    setRenaming(false);
    if (!title || title === chat?.title) return;
    try {
      await setChatTitle(chatId, title);
      setChat((prev) => (prev ? { ...prev, title } : prev));
      onAvatarChanged(); // = onChatsChanged из ChatView — обновить список чатов и шапку
    } catch (err) {
      setRenameError(err instanceof ApiError ? err.message : t('error.rename'));
    }
  };

  const toggleRoomsEnabled = async (enabled: boolean) => {
    if (roomsEnabledBusy) return;
    setRoomsEnabledBusy(true);
    setRoomsEnabledError('');
    try {
      await setChatRoomsEnabled(chatId, enabled);
      setChat((prev) => (prev ? { ...prev, hasRooms: enabled } : prev));
      onAvatarChanged(); // = onChatsChanged из ChatView — иначе RoomsColumn не подхватит флаг без перезагрузки
    } catch (err) {
      setRoomsEnabledError(err instanceof ApiError ? err.message : t('error.rooms'));
    } finally {
      setRoomsEnabledBusy(false);
    }
  };

  const doLeave = async (newOwnerId?: string) => {
    if (!me) return;
    setLeaveBusy(true);
    setLeaveError('');
    try {
      await removeMember(chatId, me.id, newOwnerId);
      onLeft();
    } catch (err) {
      setConfirmLeave(false);
      setLeaveError(err instanceof ApiError ? err.message : t('error.leave'));
    } finally {
      setLeaveBusy(false);
    }
  };

  const promoteMember = async (userId: string) => {
    setMemberActionError('');
    try {
      await setMemberRole(chatId, userId, 'admin');
      setMembers((prev) => prev?.map((m) => (m.user.id === userId ? { ...m, role: 2 } : m)) ?? prev);
    } catch (err) {
      setMemberActionError(err instanceof ApiError ? err.message : t('error.promote'));
    }
  };
  const demoteMember = async (userId: string) => {
    setMemberActionError('');
    try {
      await setMemberRole(chatId, userId, 'member');
      setMembers((prev) => prev?.map((m) => (m.user.id === userId ? { ...m, role: 3 } : m)) ?? prev);
    } catch (err) {
      setMemberActionError(err instanceof ApiError ? err.message : t('error.demote'));
    }
  };
  // Передача владения без выхода: цель → владелец, я → админ (см. ChatService.setMemberRole).
  const doTransferOwner = async () => {
    if (!ownerTarget) return;
    const targetId = ownerTarget.user.id;
    setMemberActionError('');
    try {
      await setMemberRole(chatId, targetId, 'owner');
      setMembers((prev) => prev?.map((m) => (m.user.id === targetId ? { ...m, role: 1 } : m.user.id === me?.id ? { ...m, role: 2 } : m)) ?? prev);
    } catch (err) {
      setMemberActionError(err instanceof ApiError ? err.message : t('error.transfer'));
    } finally {
      setOwnerTarget(null);
    }
  };
  const doKickMember = async () => {
    if (!kickTarget) return;
    const targetId = kickTarget.user.id;
    setMemberActionError('');
    try {
      await removeMember(chatId, targetId);
      setMembers((prev) => prev?.filter((m) => m.user.id !== targetId) ?? prev);
    } catch (err) {
      setMemberActionError(err instanceof ApiError ? err.message : t('error.kick'));
    } finally {
      setKickTarget(null);
    }
  };

  const doDelete = async () => {
    setLeaveBusy(true);
    setLeaveError('');
    try {
      await deleteChat(chatId);
      onLeft();
    } catch (err) {
      setConfirmDelete(false);
      setLeaveError(err instanceof ApiError ? err.message : t('error.delete'));
    } finally {
      setLeaveBusy(false);
    }
  };

  return (
    <div className="info-panel-backdrop" onClick={onClose}>
      <aside className="info-panel" onClick={(e) => e.stopPropagation()}>
        {!lightboxOpen && !categoryOpen && (
        <div className="info-panel-header">
          <button type="button" className="back-btn" title={t('close')} onClick={onClose}>
            <img src="/back.svg" alt={t('close')} />
          </button>
          <div className="title" style={{ flex: 1 }}>{t('info')}</div>
          {/* «Поддержку» и «Избранное» нельзя переименовать, покинуть или удалить — как в ChatList */}
          {chat?.type === 2 && !chat.isSupport && !chat.isNotes && (
            <div className="menu-wrap">
              <button type="button" className="call-mini" title={t('actions')} onClick={() => setActionsMenuOpen((v) => !v)}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>⋮</span>
              </button>
              {actionsMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setActionsMenuOpen(false)} />
                  <div className="menu">
                    {myRole != null && myRole <= 2 && (
                      <>
                        <button type="button" className="menu-item" onClick={() => { setActionsMenuOpen(false); setAddMembersOpen(true); }}>
                          {t('addMembers')}
                        </button>
                        <button type="button" className="menu-item" onClick={() => { setActionsMenuOpen(false); setInviteOpen(true); }}>
                          {t('inviteLink')}
                        </button>
                      </>
                    )}
                    {!activeRoom && myRole != null && myRole <= 2 && (
                      <button type="button" className="menu-item" onClick={startRename}>
                        {t('renameGroup')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="menu-item"
                      disabled={leaveBusy}
                      onClick={() => { setActionsMenuOpen(false); setConfirmLeave(true); }}
                    >
                      {t('leaveGroup')}
                    </button>
                    <button
                      type="button"
                      className="menu-item danger"
                      disabled={leaveBusy}
                      onClick={() => { setActionsMenuOpen(false); setConfirmDelete(true); }}
                    >
                      {t('deleteGroup')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        <div className="info-panel-body custom-scroll">
          {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
          {!chat && !error && (
            <div className="info-loading">
              <span className="spinner" />
            </div>
          )}
          {chat && (
            <>
              <div className={'info-collapse' + (categoryOpen ? ' collapsed' : '')}>
                <div className="info-collapse-inner">
                  <div className="info-profile">
                    {activeRoom ? (
                      <RoomAvatar avatarFileId={activeRoom.avatarFileId} name={activeRoom.name} size={112} />
                    ) : (
                      <Avatar
                        userId={chat.otherUserId}
                        chatId={chat.type === 2 || !chat.otherUserId ? chat.id : null}
                        name={displayTitle}
                        size={112}
                        version={avatarVersion}
                      />
                    )}
                    {!activeRoom && renaming ? (
                      <input
                        className="input" autoFocus
                        style={{ margin: '8px 16px 0', textAlign: 'center' }}
                        placeholder={t('groupName')}
                        maxLength={CHAT_TITLE_MAX_LENGTH}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') { renameCancelledRef.current = true; setRenaming(false); }
                        }}
                        onBlur={commitRename}
                      />
                    ) : (
                      <div className="info-profile-name">{activeRoom ? activeRoom.name : displayTitle}</div>
                    )}
                    <div className="info-profile-status">
                      {activeRoom
                        ? t('roomStatus', { title: chat.title })
                        : chat.type === 1 ? (presence ? formatPresence(presence) : ' ') : t('groupStatus', { count: chat.members.length })}
                    </div>
                    {activeRoom ? (
                      myRole != null && myRole <= 2 && (
                        <label className="link-btn" style={{ cursor: roomAvatarBusy ? 'default' : 'pointer', opacity: roomAvatarBusy ? 0.6 : 1 }}>
                          {roomAvatarBusy ? t('loading') : t('changeRoomAvatar')}
                          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={roomAvatarBusy} onChange={onRoomAvatarFile} />
                        </label>
                      )
                    ) : chat.type === 2 ? (
                      <label className="link-btn" style={{ cursor: avatarBusy ? 'default' : 'pointer', opacity: avatarBusy ? 0.6 : 1 }}>
                        {avatarBusy ? t('loading') : t('changeGroupAvatar')}
                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={avatarBusy} onChange={onAvatarFile} />
                      </label>
                    ) : null}
                    {!activeRoom && chat.type === 1 && chat.otherUserId && isContactState === false && (
                      <button type="button" className="link-btn" disabled={contactBusy} onClick={addContactFromInfo}>
                        {contactBusy ? t('addingContact') : t('addContact')}
                      </button>
                    )}
                  </div>
                  {avatarError && <div className="error" style={{ margin: '0 16px 12px' }}>{avatarError}</div>}
                  {renameError && <div className="error" style={{ margin: '0 16px 12px' }}>{renameError}</div>}
                  {roomAvatarError && <div className="error" style={{ margin: '0 16px 12px' }}>{roomAvatarError}</div>}

                  {!activeRoom && chat.type === 2 && myRole != null && myRole <= 2 && (
                    <>
                      <label
                        className="row"
                        style={{ alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 16px 12px', cursor: roomsEnabledBusy ? 'default' : 'pointer' }}
                      >
                        <span>
                          <div style={{ fontSize: 14 }}>{t('rooms.title')}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('rooms.description')}</div>
                        </span>
                        <span className="switch">
                          <input
                            type="checkbox"
                            checked={chat.hasRooms}
                            disabled={roomsEnabledBusy}
                            onChange={(e) => toggleRoomsEnabled(e.target.checked)}
                          />
                          <span className="switch-track"><span className="switch-thumb" /></span>
                        </span>
                      </label>
                      {roomsEnabledError && <div className="error" style={{ margin: '0 16px 12px' }}>{roomsEnabledError}</div>}
                      {chat.hasRooms && (
                        <button type="button" className="btn btn-ghost" style={{ margin: '0 16px 12px' }} onClick={() => setCreateRoomOpen(true)}>
                          {t('rooms.create')}
                        </button>
                      )}
                    </>
                  )}

                  {chat.type === 1 && otherMember && (
                    <div className="info-extra">
                      {otherMember.bio && (
                        <div className="info-extra-row">
                          <span className="info-extra-subtitle">{t('profile.bio')}</span>
                          <span className="info-extra-title" style={{ whiteSpace: 'pre-wrap' }}>
                            <EmojiText text={otherMember.bio} />
                          </span>
                        </div>
                      )}
                      <div className="info-extra-row">
                        <span className="info-extra-subtitle">{t('profile.username')}</span>
                        <span className="info-extra-title">@{otherMember.username}</span>
                      </div>
                    </div>
                  )}

                  {chat.type === 2 && (
                    <>
                      <div className="info-section-label">{t('members.title')}</div>
                      {activeRoom && (
                        <div style={{ margin: '0 16px 8px', fontSize: 12, color: 'var(--muted)' }}>
                          {t('members.roomRolesNote')}
                        </div>
                      )}
                      {memberActionError && <div className="error" style={{ margin: '0 16px 8px' }}>{memberActionError}</div>}
                      <div className="chat-info-members">
                        {/* members (с ролью) грузится отдельным запросом (см. listMembers) — пока не
                            пришёл, показываем плоский chat.members без бейджа роли, чтобы не пусто. */}
                        {(members ?? chat.members.map((u) => ({ user: u, role: 0 }))).map((m) => {
                          // Владелец управляет всеми (кроме себя); админ — только обычными участниками
                          // и только исключает: назначать/снимать админов и исключать админов может
                          // лишь владелец (см. ChatService.removeMember/setMemberRole).
                          const canManage = m.user.id !== me?.id && m.role !== 1
                            && (myRole === 1 || (myRole === 2 && m.role === 3));
                          return (
                            <div key={m.user.id} className="chat-info-member">
                              <Avatar userId={m.user.id} name={m.user.displayName} size={32} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14 }}>
                                  {m.user.displayName}
                                  {m.role === 1 && <span className="member-role-badge">{t('members.owner')}</span>}
                                  {m.role === 2 && <span className="member-role-badge">{t('members.admin')}</span>}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{m.user.username}</div>
                              </div>
                              {canManage && (
                                <div className="row-menu-wrap" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="row-menu-btn"
                                    title={t('actions')}
                                    onClick={(e) => {
                                      if (memberMenuOpenId === m.user.id) {
                                        setMemberMenuOpenId(null);
                                        return;
                                      }
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const right = window.innerWidth - rect.right;
                                      // Меню до ~4 пунктов (≈180px): не влезает вниз — открываем вверх.
                                      setMemberMenuPos(window.innerHeight - rect.bottom < 190
                                        ? { bottom: window.innerHeight - rect.top + 4, right }
                                        : { top: rect.bottom + 4, right });
                                      setMemberMenuOpenId(m.user.id);
                                    }}
                                  >
                                    ⋮
                                  </button>
                                  {memberMenuOpenId === m.user.id && memberMenuPos && (
                                    <>
                                      <div className="row-menu-backdrop" onClick={() => setMemberMenuOpenId(null)} />
                                      <div className="row-menu row-menu-fixed" style={{ ...memberMenuPos, zIndex: 60 }}>
                                        {m.role === 3 && myRole === 1 && (
                                          <button
                                            type="button"
                                            className="ctx-item"
                                            onClick={() => { setMemberMenuOpenId(null); promoteMember(m.user.id); }}
                                          >
                                            {activeRoom ? t('members.promoteGroup') : t('members.promote')}
                                          </button>
                                        )}
                                        {m.role === 2 && myRole === 1 && (
                                          <button
                                            type="button"
                                            className="ctx-item"
                                            onClick={() => { setMemberMenuOpenId(null); demoteMember(m.user.id); }}
                                          >
                                            {activeRoom ? t('members.demoteGroup') : t('members.demote')}
                                          </button>
                                        )}
                                        {myRole === 1 && m.role !== 1 && (
                                          <button
                                            type="button"
                                            className="ctx-item"
                                            onClick={() => { setMemberMenuOpenId(null); setOwnerTarget(m); }}
                                          >
                                            {t('members.makeOwner')}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="ctx-item danger"
                                          onClick={() => { setMemberMenuOpenId(null); setKickTarget(m); }}
                                        >
                                          {t('members.kick')}
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {allPinned.length > 0 && (
                    <>
                      <div className="info-section-label">{t('pinned.title')}</div>
                      <div className="info-file-list" style={{ marginBottom: 8 }}>
                        {allPinned.map(({ msg, scope }) => (
                          <PinnedRow
                            key={scope + ':' + msg.id}
                            msg={msg}
                            scope={scope}
                            onClick={() => onPreviewPinned(msg)}
                            onUnpin={() => onTogglePin(msg.id, scope, false)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="info-tabs-row">
                {categoryOpen && (
                  <button type="button" className="back-btn" title={t('collapse')} onClick={() => setCategoryOpen(false)}>
                    <img src="/back.svg" alt={t('collapse')} />
                  </button>
                )}
                <div className="info-tabs">
                  {pillRect && (
                    <div
                      className="info-tabs-pill"
                      style={{ transform: `translateX(${pillRect.left}px)`, width: pillRect.width }}
                    />
                  )}
                  {tabs.map((tab) => (
                    <button
                      key={tab.kind}
                      ref={(el) => {
                        if (el) tabRefs.current.set(tab.kind, el);
                      }}
                      type="button"
                      className={'info-tab' + (displayTab === tab.kind ? ' active' : '')}
                      onClick={() => switchTab(tab.kind)}
                    >
                      {t(tab.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={'info-collapse' + (categoryOpen ? ' collapsed' : '')}>
                <div className="info-collapse-inner">
                  {renderCollapsedPreview()}
                </div>
              </div>

              {categoryOpen && (
              <div
                className={'info-tab-body' + (panning || transitionTarget ? ' panning' : '')}
                ref={viewportRef}
                onPointerDown={onViewportPointerDown}
                onPointerMove={onViewportPointerMove}
                onPointerUp={onViewportPointerUp}
                onPointerCancel={onViewportPointerUp}
                onClickCapture={onViewportClickCapture}
              >
                {slidePrev && (
                  <div className="info-tab-slide" key={'slide-' + slidePrev}>{renderTabBody(slidePrev)}</div>
                )}
                <div className="info-tab-slide" key={'slide-' + selectedTab}>{renderTabBody(selectedTab)}</div>
                {slideNext && (
                  <div className="info-tab-slide" key={'slide-' + slideNext}>{renderTabBody(slideNext)}</div>
                )}
              </div>
              )}

              {leaveError && (
              <div className={'info-collapse' + (categoryOpen ? ' collapsed' : '')}>
                <div className="info-collapse-inner">
                  <div className="error" style={{ margin: '0 16px 16px' }}>{leaveError}</div>
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </aside>

      {pendingAvatarFile && (
        <AvatarEditor
          file={pendingAvatarFile}
          onCancel={() => setPendingAvatarFile(null)}
          onSave={onAvatarCropped}
        />
      )}

      {pendingRoomAvatarFile && (
        <AvatarEditor
          file={pendingRoomAvatarFile}
          onCancel={() => setPendingRoomAvatarFile(null)}
          onSave={onRoomAvatarCropped}
        />
      )}

      {confirmLeave && (() => {
        // Владелец уходит из группы, где есть ещё кто-то, — выбрать преемника или удалить группу.
        const others = (members ?? []).filter((m) => m.user.id !== me?.id);
        return myRole === 1 && others.length > 0 ? (
          <OwnerLeaveModal
            groupTitle={displayTitle}
            others={others}
            busy={leaveBusy}
            error={leaveError}
            onTransferAndLeave={(id) => void doLeave(id)}
            onDeleteGroup={() => void doDelete()}
            onCancel={() => setConfirmLeave(false)}
          />
        ) : (
          <ConfirmModal
            message={t('confirm.leave')}
            confirmLabel={t('confirm.leaveAction')}
            onConfirm={() => void doLeave()}
            onCancel={() => setConfirmLeave(false)}
          />
        );
      })()}
      {addMembersOpen && (
        <AddMembersModal
          chatId={chatId}
          existingIds={new Set((members ?? []).map((m) => m.user.id))}
          onInvite={() => setInviteOpen(true)}
          onClose={() => setAddMembersOpen(false)}
          onAdded={() => { listMembers(chatId).then(setMembers).catch(() => {}); }}
        />
      )}
      {inviteOpen && <InviteLinkModal chatId={chatId} onClose={() => setInviteOpen(false)} />}
      {createRoomOpen && (
        <RoomEditModal
          chatId={chatId}
          room={null}
          onClose={() => setCreateRoomOpen(false)}
          onCreated={(r) => { setRooms((prev) => [...prev, r]); notifyRoomsChanged(chatId); }}
          onUpdated={() => {}}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          message={t('confirm.delete')}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      {ownerTarget && (
        <ConfirmModal
          message={t('confirm.transfer', { name: ownerTarget.user.displayName })}
          confirmLabel={t('confirm.transferAction')}
          onConfirm={doTransferOwner}
          onCancel={() => setOwnerTarget(null)}
        />
      )}
      {kickTarget && (
        <ConfirmModal
          message={t('confirm.kick', { name: kickTarget.user.displayName })}
          confirmLabel={t('confirm.kickAction')}
          onConfirm={doKickMember}
          onCancel={() => setKickTarget(null)}
        />
      )}

      {fileCtxMenu && (
        <>
          <div
            className="ctx-backdrop"
            onClick={() => setFileCtxMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setFileCtxMenu(null);
            }}
          />
          <div className="ctx-menu" style={{ left: fileCtxMenu.x, top: fileCtxMenu.y }}>
            <button
              className="ctx-item"
              onClick={() => {
                onJumpToMessage(fileCtxMenu.msgId);
                setFileCtxMenu(null);
              }}
            >
              <img src="/reply.svg" alt="" /> {t('misc:chatInfoJumpToMessage')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
